import { z } from 'zod';

/**
 * Contrats de DESSIN temps réel (ADR 0014) — vectoriels, déterministes,
 * rejouables. Coordonnées normalisées sur un canevas logique à ratio fixe (4:3),
 * quantifiées sur 12 bits → rendu identique quelle que soit la taille d'écran
 * (mobile/desktop). Palette et tailles de pinceau BORNÉES et partagées : le
 * client comme le serveur s'accordent sur les mêmes index. Aucun secret ici (ce
 * sont des coordonnées).
 */

/** Repère logique (le rendu client mappe ce repère à la taille réelle). */
export const DRAW_CANVAS_W = 4096;
export const DRAW_CANVAS_H = 3072; // ratio 4:3
/** Coordonnée max (12 bits : 0..4095). */
export const DRAW_COORD_MAX = 4095;

/** Palette bornée (index 0..N-1). Contrastée, testée daltonisme côté design (spec 05). */
export const DRAW_PALETTE = [
  '#1b1b1f', // presque noir
  '#ffffff', // blanc
  '#e11d48', // rouge
  '#f97316', // orange
  '#f5c518', // jaune
  '#22c55e', // vert
  '#0ea5e9', // bleu ciel
  '#3b82f6', // bleu
  '#8b5cf6', // violet
  '#ec4899', // rose
  '#a16207', // brun (panda roux)
  '#9ca3af', // gris
] as const;

/** Tailles de pinceau (index 0..M-1) en unités logiques. */
export const DRAW_BRUSH_SIZES = [4, 10, 22, 44] as const;

/** Bornes anti-abus / charge (ADR 0014 §4). */
export const DRAW_MAX_POINTS_PER_STROKE = 400; // nombres dans `pts` (pairs → ≤ 200 points)
export const DRAW_MAX_OPS_PER_BATCH = 60; // ops par event `draw:ops`
export const DRAW_MAX_OPS_PER_ROUND = 3000; // ops de contenu conservées par manche (journal)
/** Cap des ops de contrôle (undo/clear) par manche : borne le flood re-diffusé (audit M2). */
export const DRAW_MAX_CONTROL_OPS_PER_ROUND = 500;
/** Plafond de taille d'un message WS (défense en profondeur, abaissé du défaut 1 Mo). */
export const DRAW_WS_MAX_BUFFER_BYTES = 262_144; // 256 Ko

/** Opération ENTRANTE (du dessinateur) — sans `seq` (attribué par le serveur). */
export type DrawOpInput =
  | { t: 'stroke'; color: number; size: number; pts: number[] } // pts = [x0,y0,x1,y1,…]
  | { t: 'fill'; color: number; x: number; y: number }
  | { t: 'undo' }
  | { t: 'clear' };

/** Opération STOCKÉE / DIFFUSÉE — horodatée par le serveur (seq monotone par manche). */
export type DrawOp = DrawOpInput & { seq: number };

/** Noms d'events de dessin (socle réutilisable, ADR 0014 §3). */
export const DrawEvents = {
  /** dessinateur → serveur (batch) ; serveur → room (volatile, sauf dessinateur). */
  Ops: 'draw:ops',
  /** client → serveur : rattrapage du journal (rejeu accéléré). */
  Sync: 'draw:sync',
} as const;

const coord = z.number().int().min(0).max(DRAW_COORD_MAX);
const colorIndex = z.number().int().min(0).max(DRAW_PALETTE.length - 1);
const sizeIndex = z.number().int().min(0).max(DRAW_BRUSH_SIZES.length - 1);

/** Schéma zod STRICT d'une op entrante (rejette toute clé inconnue, borne tout). */
export const drawOpInputSchema = z.discriminatedUnion('t', [
  z
    .object({
      t: z.literal('stroke'),
      color: colorIndex,
      size: sizeIndex,
      pts: z
        .array(coord)
        .min(2)
        .max(DRAW_MAX_POINTS_PER_STROKE)
        .refine((p) => p.length % 2 === 0, 'pts doit contenir des paires (x,y)'),
    })
    .strict(),
  z.object({ t: z.literal('fill'), color: colorIndex, x: coord, y: coord }).strict(),
  z.object({ t: z.literal('undo') }).strict(),
  z.object({ t: z.literal('clear') }).strict(),
]);

/** Payload de `draw:ops` (batch borné). */
export const drawOpsPayloadSchema = z
  .object({
    matchId: z.string().min(1).max(64),
    ops: z.array(drawOpInputSchema).min(1).max(DRAW_MAX_OPS_PER_BATCH),
  })
  .strict();

export type DrawOpsPayload = z.infer<typeof drawOpsPayloadSchema>;

export const drawSyncPayloadSchema = z.object({ matchId: z.string().min(1).max(64) }).strict();

/** Payload de rattrapage renvoyé par le serveur (`draw:sync` en ack). */
export interface DrawSyncSnapshot {
  ops: DrawOp[];
  seq: number;
}
